import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, Edit3, Trash2, ChevronLeft, Briefcase, Users, Mail, Phone,
  Download, CheckCircle2, X, Star, Search, Image as ImageIcon, Video,
  ChevronDown, ChevronRight,
} from "lucide-react";
import Pager, { usePager } from "@/components/Pager";
import MarkdownEditor from "@/components/MarkdownEditor";

const EMPLOYMENT_TYPES = ["Full-time", "Part-time", "Contract", "Internship"];
const WORK_MODES = ["Remote", "Hybrid", "On-site"];

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "new", label: "New" },
  { value: "shortlisted", label: "Shortlisted" },
  { value: "interview", label: "Interview" },
  { value: "hired", label: "Hired" },
  { value: "rejected", label: "Rejected" },
];

const STATUS_BADGE = {
  new: "border-sky-200 bg-sky-50 text-sky-700",
  shortlisted: "border-amber-200 bg-amber-50 text-amber-700",
  interview: "border-emerald-200 bg-emerald-50 text-emerald-700",
  hired: "border-violet-200 bg-violet-50 text-violet-700",
  rejected: "border-rose-200 bg-rose-50 text-rose-700",
};

const EMPTY_VAC = {
  title: "", department: "", location: "Kathmandu, Nepal",
  employment_type: ["Full-time"], work_modes: ["On-site"],
  description: "", closes_at: "", is_active: true,
  poster_base64: "", poster_mime: "",
};

const MAX_POSTER_BYTES = 1024 * 1024; // 1 MB raw image

function fileToBase64(f) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(f);
  });
}

export default function AdminVacancies() {
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [selectedVacancy, setSelectedVacancy] = useState(null);
  const [applications, setApplications] = useState([]);  const [appsLoading, setAppsLoading] = useState(false);
  const [expandedCovers, setExpandedCovers] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState({});

  // Decision modal (interview / reject / shortlist) — replaces window.prompt
  const [decisionModal, setDecisionModal] = useState(null);
  // shape: { application, decision, interview_at, note }

  const loadVacancies = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/vacancies");
      setVacancies(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadVacancies(); }, []);

  const loadApplications = async () => {
    if (!selectedVacancy) return;
    setAppsLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const r = await api.get(`/admin/vacancies/${selectedVacancy.id}/applications`, { params });
      setApplications(r.data);
    } catch (e) {
      toast.error("Couldn't load applications");
    } finally { setAppsLoading(false); }
  };
  useEffect(() => {
    if (selectedVacancy) loadApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVacancy?.id, statusFilter]);
  useEffect(() => {
    if (!selectedVacancy) return;
    const t = setTimeout(loadApplications, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const pager = usePager(applications, 10);

  // ---- Vacancy CRUD handlers ----
  const startCreate = () => setEditing({ ...EMPTY_VAC });
  const startEdit = (v) => setEditing({
    ...EMPTY_VAC,
    ...v,
    // Coerce legacy single-string fields into arrays so checkboxes render.
    employment_type: Array.isArray(v.employment_type) ? v.employment_type : (v.employment_type ? [v.employment_type] : []),
    work_modes: Array.isArray(v.work_modes) ? v.work_modes : (v.work_modes ? [v.work_modes] : []),
    poster_base64: "",
    poster_mime: "",
    poster_url: v.poster_url || "",
  });
  const cancelEdit = () => setEditing(null);

  const toggleInList = (list, value) => (
    (list || []).includes(value)
      ? (list || []).filter((x) => x !== value)
      : [...(list || []), value]
  );

  const onPosterPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Please upload an image file"); return; }
    if (f.size > MAX_POSTER_BYTES) { toast.error("Poster too large; max 1MB"); return; }
    try {
      const b64 = await fileToBase64(f);
      setEditing((s) => ({
        ...s,
        poster_base64: b64,
        poster_mime: f.type,
        poster_url: `data:${f.type};base64,${b64}`,
      }));
    } catch { toast.error("Couldn't read image"); }
  };
  const clearPoster = () => setEditing((s) => ({ ...s, poster_base64: "", poster_mime: "", poster_url: "" }));

  const saveVacancy = async (e) => {
    e.preventDefault();
    if (!editing.title.trim() || !editing.description.trim()) {
      toast.error("Title and description are required");
      return;
    }
    // Strip the local-preview-only `poster_url` field before sending.
    const { poster_url, ...payload } = editing; // eslint-disable-line no-unused-vars
    // If editing and user didn't re-upload, omit poster fields so backend keeps existing.
    if (editing.id && !payload.poster_base64) {
      delete payload.poster_base64;
      delete payload.poster_mime;
    }
    try {
      if (editing.id) {
        await api.put(`/admin/vacancies/${editing.id}`, payload);
        toast.success("Vacancy updated");
      } else {
        await api.post("/admin/vacancies", payload);
        toast.success("Vacancy created");
      }
      setEditing(null);
      loadVacancies();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const deleteVacancy = async (v) => {
    if (!window.confirm(`Delete "${v.title}"? All ${v.applicant_count || 0} application(s) will also be removed.`)) return;
    try {
      await api.delete(`/admin/vacancies/${v.id}`);
      toast.success("Deleted");
      loadVacancies();
      if (selectedVacancy?.id === v.id) setSelectedVacancy(null);
    } catch (e) { toast.error("Couldn't delete"); }
  };

  const toggleActive = async (v) => {
    try {
      await api.put(`/admin/vacancies/${v.id}`, { ...v, is_active: !v.is_active });
      loadVacancies();
    } catch (e) { toast.error("Couldn't update"); }
  };

  // ---- Application handlers ----
  const downloadCv = async (a) => {
    try {
      const r = await api.get(`/admin/applications/${a.id}/cv`);
      const link = document.createElement("a");
      link.href = `data:${r.data.mime};base64,${r.data.content_base64}`;
      link.download = r.data.filename || `cv_${a.name}.pdf`;
      link.click();
    } catch (e) { toast.error("Couldn't download CV"); }
  };

  const deleteApplication = async (a) => {
    if (!window.confirm(`Delete application from "${a.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/applications/${a.id}`);
      setApplications((arr) => arr.filter((x) => x.id !== a.id));
      toast.success("Application deleted");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // Open the decision modal (interview / reject / shortlist) for a candidate.
  const openDecision = (application, decision) => {
    setDecisionModal({
      application,
      decision,
      interview_at: "",
      note: "",
    });
  };

  const submitDecision = async () => {
    if (!decisionModal) return;
    const { application: a, decision, note, interview_at } = decisionModal;
    setBusy((b) => ({ ...b, [a.id]: decision }));
    try {
      const r = await api.post(`/admin/applications/${a.id}/decision`, {
        decision, note, interview_at: decision === "interview" ? interview_at : "",
      });
      if (r.data?.email?.ok) toast.success(`Decision recorded · email sent (${r.data.email.provider})`);
      else toast.success("Decision recorded · email may not have been delivered");
      setDecisionModal(null);
      loadApplications();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update");
    } finally {
      setBusy((b) => ({ ...b, [a.id]: null }));
    }
  };

  // Decide which action buttons should be visible based on current status.
  const visibleActions = (status) => {
    if (status === "new") return ["shortlist", "interview", "reject"];
    if (status === "shortlisted") return ["interview", "reject"];
    if (status === "interview") return ["hire", "reject"];
    if (status === "rejected") return ["hire"]; // allow admin to revert a rejection and hire
    return []; // 'hired' terminal
  };

  // ============================= RENDER =============================
  // Drill-down view (applications for one vacancy)
  if (selectedVacancy) {
    return (
      <div data-testid="admin-applications-page">
        <button
          onClick={() => { setSelectedVacancy(null); setApplications([]); setSearch(""); setStatusFilter("all"); }}
          className="kb-mono text-xs text-kb-ink-muted hover:text-kb-primary inline-flex items-center gap-2"
          data-testid="admin-apps-back-btn"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> BACK TO ALL VACANCIES
        </button>
        <h1 className="font-serif text-3xl text-kb-ink mt-3">{selectedVacancy.title}</h1>
        <div className="kb-mono text-[11px] text-kb-ink-muted mt-1">
          {selectedVacancy.applicant_count} application{selectedVacancy.applicant_count === 1 ? "" : "s"} · {selectedVacancy.new_count} new
        </div>

        <div className="mt-6 flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-kb-ink-light" />
            <input
              className="kb-input pl-9"
              placeholder="Search by name, email, cover letter…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="admin-apps-search-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                className={`kb-mono text-[10px] uppercase tracking-wider px-3 py-2 rounded-sm border ${
                  statusFilter === f.value
                    ? "border-kb-primary bg-kb-primary text-white"
                    : "border-slate-200 bg-white text-kb-ink-muted hover:border-kb-primary"
                }`}
                data-testid={`admin-apps-filter-${f.value}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {appsLoading ? (
          <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div>
        ) : applications.length === 0 ? (
          <div className="mt-8 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">No applications match.</div>
        ) : (
          <div className="mt-6 space-y-3">
            {pager.pageItems.map((a) => {
              const actions = visibleActions(a.status);
              return (
                <div key={a.id} className="kb-card p-5" data-testid={`admin-app-${a.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-base font-medium text-kb-ink">{a.name}</div>
                        <span className={`kb-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${STATUS_BADGE[a.status] || "border-slate-200"}`}>{a.status}</span>
                      </div>
                      <div className="kb-mono text-[11px] text-kb-ink-muted mt-1 flex flex-wrap items-center gap-3">
                        <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {a.email}</span>
                        {a.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {a.phone}</span>}
                        <span>· {new Date(a.created_at).toLocaleDateString()}</span>
                        {a.interview_at && (
                          <span className="text-emerald-700">· Interview: {new Date(a.interview_at).toLocaleString()}</span>
                        )}
                      </div>
                      {a.cover_letter && (
                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={() => setExpandedCovers((m) => ({ ...m, [a.id]: !m[a.id] }))}
                            className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1"
                            data-testid={`admin-app-${a.id}-cover-toggle`}
                          >
                            {expandedCovers[a.id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                            Cover letter
                          </button>
                          {expandedCovers[a.id] ? (
                            <p className="text-sm text-kb-ink-muted mt-2 whitespace-pre-line" data-testid={`admin-app-${a.id}-cover-full`}>{a.cover_letter}</p>
                          ) : (
                            <p className="text-sm text-kb-ink-muted mt-1 line-clamp-2" data-testid={`admin-app-${a.id}-cover-preview`}>{a.cover_letter}</p>
                          )}
                        </div>
                      )}
                      {a.admin_notes && (
                        <div className="mt-2 text-[11px] text-kb-ink-light italic">Internal note: {a.admin_notes}</div>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-slate-200 flex flex-wrap gap-2">
                    <button onClick={() => downloadCv(a)} className="kb-btn-outline text-xs" data-testid={`admin-app-${a.id}-cv-btn`}>
                      <Download className="w-3.5 h-3.5" /> Download CV
                    </button>
                    {a.interview_meet_url && a.status === "interview" && (
                      <a
                        href={a.interview_meet_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 rounded-sm kb-mono uppercase tracking-wider inline-flex items-center gap-1.5"
                        data-testid={`admin-app-${a.id}-join-meet-btn`}
                      >
                        <Video className="w-3.5 h-3.5" /> Join interview
                      </a>
                    )}
                    {actions.includes("shortlist") && (
                      <button onClick={() => openDecision(a, "shortlisted")} disabled={busy[a.id]}
                        className="kb-btn-outline text-xs" data-testid={`admin-app-${a.id}-shortlist-btn`}>
                        <Star className="w-3.5 h-3.5" /> Shortlist
                      </button>
                    )}
                    {actions.includes("interview") && (
                      <button onClick={() => openDecision(a, "interview")} disabled={busy[a.id]}
                        className="kb-btn-primary text-xs" data-testid={`admin-app-${a.id}-interview-btn`}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Invite to interview
                      </button>
                    )}
                    {actions.includes("hire") && (
                      <button onClick={() => openDecision(a, "hired")} disabled={busy[a.id]}
                        className="text-xs text-white bg-violet-600 hover:bg-violet-700 px-3 py-1.5 rounded-sm kb-mono uppercase tracking-wider inline-flex items-center gap-1.5"
                        data-testid={`admin-app-${a.id}-hire-btn`}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> {a.status === "rejected" ? "Revert & hire" : "Hire & onboard"}
                      </button>
                    )}
                    {actions.includes("reject") && (
                      <button onClick={() => openDecision(a, "rejected")} disabled={busy[a.id]}
                        className="text-xs text-rose-600 border border-rose-200 hover:bg-rose-50 px-3 py-1.5 rounded-sm kb-mono uppercase tracking-wider"
                        data-testid={`admin-app-${a.id}-reject-btn`}>
                        <X className="w-3.5 h-3.5 inline" /> Reject
                      </button>
                    )}
                    {actions.length === 0 && (
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light italic">No further actions for this status.</div>
                    )}
                    <button
                      onClick={() => deleteApplication(a)}
                      className="ml-auto text-xs text-rose-600 border border-rose-200 hover:bg-rose-50 px-3 py-1.5 rounded-sm kb-mono uppercase tracking-wider inline-flex items-center gap-1.5"
                      data-testid={`admin-app-${a.id}-delete-btn`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
            <Pager {...pager} label="applications" testId="admin-apps-pager" />
          </div>
        )}

        {/* Decision modal */}
        {decisionModal && (
          <DecisionModal
            decisionModal={decisionModal}
            setDecisionModal={setDecisionModal}
            submitDecision={submitDecision}
            busy={busy[decisionModal.application.id]}
          />
        )}
      </div>
    );
  }

  // ===================== Main view (vacancies list + editor) =====================
  return (
    <div data-testid="admin-vacancies-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">CAREERS</div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Open positions & <em className="italic text-kb-primary">applicants</em></h1>
        </div>
        {!editing && (
          <button onClick={startCreate} className="kb-btn-primary" data-testid="admin-vacancy-new-btn">
            <Plus className="w-4 h-4" /> New vacancy
          </button>
        )}
      </div>

      {/* Editor */}
      {editing && (
        <form onSubmit={saveVacancy} className="kb-card p-6 mt-6 space-y-4" data-testid="admin-vacancy-editor">
          <h2 className="font-serif text-xl text-kb-ink">{editing.id ? "Edit vacancy" : "Create vacancy"}</h2>

          {/* Poster upload — square */}
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Vacancy poster (square · max 1MB)</label>
            <div className="mt-2 flex items-start gap-4 flex-wrap">
              <div className="w-32 h-32 rounded-sm border border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden">
                {editing.poster_url ? (
                  <img src={editing.poster_url} alt="" className="w-full h-full object-cover" data-testid="vacancy-poster-preview" />
                ) : (
                  <ImageIcon className="w-7 h-7 text-kb-ink-light" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label className="kb-btn-outline text-sm cursor-pointer inline-flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" /> {editing.poster_url ? "Replace poster" : "Upload poster"}
                  <input type="file" accept="image/*" className="hidden" onChange={onPosterPick} data-testid="vacancy-poster-input" />
                </label>
                {editing.poster_url && (
                  <button type="button" onClick={clearPoster} className="kb-mono text-[10px] uppercase tracking-wider text-rose-600 hover:text-rose-700 inline-flex items-center gap-1" data-testid="vacancy-poster-clear">
                    <X className="w-3 h-3" /> Remove
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Title *</label>
              <input required className="kb-input mt-1" value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="admin-vacancy-title" />
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Department</label>
              <input className="kb-input mt-1" placeholder="e.g. Engineering"
                value={editing.department || ""} onChange={(e) => setEditing({ ...editing, department: e.target.value })} />
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Location</label>
              <input className="kb-input mt-1" value={editing.location || ""}
                onChange={(e) => setEditing({ ...editing, location: e.target.value })} />
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Employment type (multi)</label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="vacancy-employment-type-group">
                {EMPLOYMENT_TYPES.map((t) => {
                  const active = (editing.employment_type || []).includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setEditing({ ...editing, employment_type: toggleInList(editing.employment_type, t) })}
                      className={`kb-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors ${
                        active ? "border-kb-primary bg-kb-primary text-white" : "border-slate-200 bg-white text-kb-ink-muted hover:border-kb-primary"
                      }`}
                      data-testid={`vacancy-employment-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Work mode (multi)</label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="vacancy-work-modes-group">
                {WORK_MODES.map((t) => {
                  const active = (editing.work_modes || []).includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setEditing({ ...editing, work_modes: toggleInList(editing.work_modes, t) })}
                      className={`kb-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors ${
                        active ? "border-kb-primary bg-kb-primary text-white" : "border-slate-200 bg-white text-kb-ink-muted hover:border-kb-primary"
                      }`}
                      data-testid={`vacancy-work-mode-${t.toLowerCase()}`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Closes at (optional)</label>
              <input type="date" className="kb-input mt-1" value={(editing.closes_at || "").slice(0, 10)}
                onChange={(e) => setEditing({ ...editing, closes_at: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Description (markdown) *</label>
              <div className="mt-1">
                <MarkdownEditor
                  value={editing.description}
                  onChange={(v) => setEditing({ ...editing, description: v })}
                  rows={12}
                  testId="admin-vacancy-desc"
                  placeholder="# About the role&#10;&#10;## Responsibilities&#10;- …&#10;&#10;## Requirements&#10;- …&#10;&#10;## Compensation&#10;NPR 60K – 80K based on experience"
                />
              </div>
            </div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted inline-flex items-center gap-2">
              <input type="checkbox" checked={editing.is_active}
                onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} />
              ACTIVE (visible on public careers page)
            </label>
          </div>
          <div className="flex gap-2 pt-3 border-t border-slate-200">
            <button type="submit" className="kb-btn-primary text-sm" data-testid="admin-vacancy-save-btn">
              <CheckCircle2 className="w-4 h-4" /> {editing.id ? "Save changes" : "Create vacancy"}
            </button>
            <button type="button" onClick={cancelEdit} className="kb-btn-outline text-sm">Cancel</button>
          </div>
        </form>
      )}

      {/* Vacancies list */}
      {loading ? (
        <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading vacancies…</div>
      ) : vacancies.length === 0 ? (
        <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white">
          <Briefcase className="w-8 h-8 text-kb-ink-light mx-auto" strokeWidth={1.5} />
          <p className="mt-3 text-sm text-kb-ink-muted">No vacancies yet — create the first one to start collecting applications.</p>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {vacancies.map((v) => (
            <div key={v.id} className="kb-card p-5" data-testid={`admin-vacancy-${v.id}`}>
              <div className="flex flex-wrap items-start gap-4">
                {v.poster_url && (
                  <img src={v.poster_url} alt="" className="w-20 h-20 rounded-sm object-cover border border-slate-200 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-serif text-lg text-kb-ink">{v.title}</h3>
                    <span className={`kb-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
                      v.is_active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-50 text-slate-500"
                    }`}>
                      {v.is_active ? "ACTIVE" : "INACTIVE"}
                    </span>
                  </div>
                  <div className="kb-mono text-[11px] text-kb-ink-muted mt-1">
                    {v.department || "—"} · {v.location}
                    {(v.employment_type || []).length > 0 && <> · {(v.employment_type || []).join(", ")}</>}
                    {(v.work_modes || []).length > 0 && <> · {(v.work_modes || []).join(" / ")}</>}
                  </div>
                  <p className="text-sm text-kb-ink-muted mt-2 line-clamp-2">{v.description}</p>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <button
                    onClick={() => setSelectedVacancy(v)}
                    className="kb-btn-primary text-xs"
                    data-testid={`admin-vacancy-${v.id}-apps-btn`}
                  >
                    <Users className="w-3.5 h-3.5" /> {v.applicant_count} applicant{v.applicant_count === 1 ? "" : "s"}
                    {v.new_count > 0 && <span className="ml-1 inline-block bg-white text-kb-primary text-[10px] px-1.5 py-0.5 rounded-sm font-bold">{v.new_count} new</span>}
                  </button>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-slate-200 flex flex-wrap gap-2">
                <button onClick={() => startEdit(v)} className="kb-btn-outline text-xs" data-testid={`admin-vacancy-${v.id}-edit-btn`}>
                  <Edit3 className="w-3 h-3" /> Edit
                </button>
                <button onClick={() => toggleActive(v)} className="kb-btn-outline text-xs">
                  {v.is_active ? "Deactivate" : "Activate"}
                </button>
                <button onClick={() => deleteVacancy(v)} className="text-xs text-rose-600 border border-rose-200 hover:bg-rose-50 px-3 py-1.5 rounded-sm kb-mono uppercase tracking-wider"
                  data-testid={`admin-vacancy-${v.id}-delete-btn`}>
                  <Trash2 className="w-3 h-3 inline" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Decision modal — date-time picker for interview, note textarea for all decisions. */
function DecisionModal({ decisionModal, setDecisionModal, submitDecision, busy }) {
  const { application: a, decision, interview_at, note } = decisionModal;
  const titleMap = {
    interview: "Invite to interview",
    rejected: "Reject application",
    shortlisted: "Shortlist candidate",
    hired: "Hire & onboard",
  };
  const ctaMap = {
    interview: "Send invite + email",
    rejected: "Send rejection email",
    shortlisted: "Shortlist + email",
    hired: "Create account + send onboarding",
  };
  const ctaCls = decision === "rejected"
    ? "text-xs text-white bg-rose-600 hover:bg-rose-700 px-4 py-2 rounded-sm kb-mono uppercase tracking-wider"
    : "kb-btn-primary text-xs";
  return (
    <div className="fixed inset-0 z-[150] bg-black/40 flex items-center justify-center p-4" data-testid="decision-modal">
      <div className="bg-white rounded-sm shadow-2xl max-w-md w-full border border-slate-200">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-serif text-lg text-kb-ink">{titleMap[decision]}</h3>
          <button onClick={() => setDecisionModal(null)} className="p-1 text-kb-ink-muted hover:text-kb-ink" data-testid="decision-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div className="kb-mono text-[11px] text-kb-ink-muted">
            Candidate: <span className="text-kb-ink font-medium">{a.name}</span> · {a.email}
          </div>
          {decision === "interview" && (
            <div>
              <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Interview date &amp; time</label>
              <input
                type="datetime-local"
                className="kb-input mt-1"
                value={interview_at || ""}
                onChange={(e) => setDecisionModal({ ...decisionModal, interview_at: e.target.value })}
                data-testid="decision-interview-at-input"
              />
              <p className="kb-mono text-[10px] text-kb-ink-light mt-1">Sent in the candidate&apos;s timezone via email.</p>
            </div>
          )}
          <div>
            <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
              {decision === "rejected" ? "Optional personal note (kept respectful)" : "Personal note (optional)"}
            </label>
            <textarea
              rows={4}
              className="kb-input mt-1 resize-none"
              placeholder={decision === "interview"
                ? "e.g. We're excited to chat about your work on…"
                : decision === "rejected"
                  ? "e.g. We loved your enthusiasm but are looking for someone with more X experience."
                  : "Anything you'd like the candidate to know…"}
              value={note}
              onChange={(e) => setDecisionModal({ ...decisionModal, note: e.target.value })}
              data-testid="decision-note-input"
            />
          </div>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={() => setDecisionModal(null)} className="kb-btn-outline text-xs" data-testid="decision-cancel-btn">Cancel</button>
          <button
            onClick={submitDecision}
            disabled={busy || (decision === "interview" && !interview_at)}
            className={ctaCls + " disabled:opacity-50"}
            data-testid="decision-submit-btn"
          >
            {busy ? "Sending…" : ctaMap[decision]}
          </button>
        </div>
      </div>
    </div>
  );
}
